import React, { useCallback, useEffect, useState } from 'react';
import {
  listRewardFulfillment,
  completeRewardFulfillment,
  cancelRewardFulfillment,
} from './adminApi';

// Reward fulfillment queue - every pending row is a redemption the customer
// already PAID points for (they're holding a confirmation code and an email
// that says "our team will follow up"). Big external rewards - Disney
// tickets, airline credits, trips, merch - can't be granted by the app, so a
// human buys/sends the real thing and marks the row Fulfilled here. Cancel
// refunds the points and returns the unit to catalog inventory.

const STATUS_TABS = ['pending', 'completed', 'cancelled'];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function Row({ item, secret, onResolved }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null); // 'complete' | 'cancel'
  const [error, setError] = useState(null);
  const reward = item.reward || {};
  const code = item.metadata?.redemptionCode;
  const isPending = item.status === 'pending';

  const act = async (action) => {
    if (action === 'cancel' && !window.confirm(
      `Cancel this redemption and refund ${Number(item.pointsCost).toLocaleString()} points to user ${item.userId}?`
    )) return;
    setBusy(action);
    setError(null);
    try {
      if (action === 'complete') await completeRewardFulfillment(item._id, 'admin-console', note, secret);
      else await cancelRewardFulfillment(item._id, 'admin-console', note, secret);
      onResolved();
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  return (
    <div className="adm-detail-card" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong style={{ fontSize: 15 }}>{reward.name || item.sku}</strong>
          <span className={`adm-status-pill adm-status-${isPending ? 'pending' : item.status === 'completed' ? 'approved' : 'rejected'}`} style={{ marginLeft: 10 }}>
            {item.status}
          </span>
          <div className="adm-section-note" style={{ marginTop: 4 }}>
            {reward.type || item.rewardType} · SKU {item.sku}
            {item.metadata?.packageName ? ` · package: ${item.metadata.packageName}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>{Number(item.pointsCost).toLocaleString()} pts · user #{item.userId}</div>
          <div className="adm-section-note">{formatDate(item.redemptionDate)}</div>
        </div>
      </div>

      <div className="adm-detail-row" style={{ marginTop: 10 }}>
        <span>Confirmation code (customer has this)</span>
        <code>{code || '—'}</code>
      </div>
      {item.expirationDate && (
        <div className="adm-detail-row">
          <span>Redemption window ends</span>
          <span>{formatDate(item.expirationDate)}</span>
        </div>
      )}
      {!isPending && (
        <div className="adm-detail-row">
          <span>Resolved</span>
          <span>
            {formatDate(item.metadata?.resolvedAt)} by {item.metadata?.resolvedBy || '—'}
            {item.metadata?.resolutionNote ? ` — “${item.metadata.resolutionNote}”` : ''}
          </span>
        </div>
      )}

      {isPending && (
        <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Note (e.g. order #, voucher sent to email)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '8px 10px', borderRadius: 8, border: '1px solid #333', background: '#14142a', color: '#fff' }}
          />
          <button className="adm-review-btn" disabled={!!busy} onClick={() => act('complete')}>
            {busy === 'complete' ? 'Saving…' : '✓ Fulfilled'}
          </button>
          <button className="adm-review-btn" disabled={!!busy} onClick={() => act('cancel')} style={{ background: '#5a1f2b' }}>
            {busy === 'cancel' ? 'Refunding…' : '✕ Cancel + Refund'}
          </button>
        </div>
      )}
      {error && <p className="adm-section-note" style={{ color: '#ff8080', marginTop: 8 }}>{error}</p>}
    </div>
  );
}

export default function RewardFulfillmentPanel({ secret }) {
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listRewardFulfillment(status, secret)
      .then(setItems)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [status, secret]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Reward Fulfillment</h1>
          <p className="adm-page-subtitle">
            Redemptions the app can't grant by itself - the customer already paid points and is waiting
            on a real Disney ticket, gift card, trip, or package. Fulfill after you've sent it; Cancel refunds them.
          </p>
        </div>
        <button className="adm-review-btn" onClick={load}>↻ Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            className="adm-review-btn"
            onClick={() => setStatus(tab)}
            style={status === tab ? {} : { opacity: 0.5 }}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="adm-section-note">Loading…</p>
      ) : error ? (
        <p className="adm-section-note" style={{ color: '#ff8080' }}>{error}</p>
      ) : items.length === 0 ? (
        <p className="adm-section-note">
          {status === 'pending' ? 'Nothing waiting - every promised reward has been handled.' : `No ${status} redemptions.`}
        </p>
      ) : (
        items.map((item) => <Row key={item._id} item={item} secret={secret} onResolved={load} />)
      )}
    </div>
  );
}
